<script lang="ts">
    import {
        CheckIcon,
        PlusCircleIcon,
        MinusCircleIcon,
        ChevronDownIcon,
        SettingsIcon,
        SlidersIcon,
        XIcon,
    } from "svelte-feather-icons";
    import BlackSquare from "../icons/BlackSquare.svelte";
    import BlueSquare from "../icons/BlueSquare.svelte";
    import GreenCircle from "../icons/GreenCircle.svelte";
    import { QuantizedScore, SearchResult, SearchResponse, search_ws, search_by_name_ws, search } from "../search";
    import QuestionsOverview from "./QuestionsOverview.svelte";
    import ResortDetails from "./ResortDetails.svelte";
    import { navigate, navigateBack } from "../routing/index";
    import { TransportMode } from "../transport_mode.js";
    import { Toplist, toplistCriteriaParser } from "../toplist_criteria";
    import { itemTransition, itemTransitionIn, itemTransitionOut, ListElement } from "../animations";
    import type { RouteLocation } from "../routing/route_location";
    import { onDestroy, onMount, tick } from "svelte/internal";
    import { Socket } from "../socket";
    import AnimatedList from "./AnimatedList.svelte";
    import TreeBackground from "./TreeBackground.svelte";
    import LoadingSpinner from "./LoadingSpinner.svelte";
    import { debounce } from "ts-debounce";
    import { Any, dict, Re, S, seq, union } from "../combinators";
    import {
        RouteResort,
        RouteSearch,
        RouteSettings,
        RouteToplist,
        urlToString,
        URLComponents,
    } from "../search_routing";

    const DefaultLimit = 18;

    let animatedList: AnimatedList<SearchResult> | null = null;
    let renderedItems: ListElement<SearchResult>[] = [];

    let searchResults: SearchResponse | null = null;
    let title = "Alla skidorter - Fjälltoppen";
    let description = "Alla Sveriges skidorter samlade på ett ställe.";
    const socketProtocol = window.location.protocol == "https:" ? "wss:" : "ws:";
    let socket = new Socket(`${socketProtocol}//${window.location.host}/socket`);
    let topGroupingHeight = 800;
    let query = "";

    const updateSearchResults = (newResults: SearchResponse) => {
        searchResults = newResults;
    };

    const refresh = (query: string) => {
        search_by_name_ws(socket, query).then(updateSearchResults);

        limit = DefaultLimit;
    };

    const debouncedRefresh = debounce(refresh, 50);

    const navigateToResort = (resortId: string) => {
        if (plausible) {
            plausible("Open resort details", { props: { resortId } });
        }
        if (searchResults) {
            // Let the server know that we selected this resort
            fetch("/resortdetails?resort=" + resortId + "&query=" + searchResults.id);
        }

        let url = urlToString({
            type: "resort",
            resort: resortId,
        });
        navigate(url);
        if (innerWidth < 800) {
            // TODO: Figure out a way to return to the correct scroll position in the results list when clicking the back button.
            window.scrollTo(0, 0);
        }
    };

    let limit: number = DefaultLimit;

    let innerWidth: number = 0;

    $: {
        debouncedRefresh(query);
    }
</script>

<svelte:window bind:innerWidth />

<main>
    <TreeBackground />

    {#if searchResults === null}
        <div class="search-progress">
            <LoadingSpinner />
            <span>Hämtar Sveriges skidorter...</span>
        </div>
    {:else}
        <input type="text" class="autocomplete-input" placeholder="Sök skidort" bind:value={query} />
        <div class="search-results-wrapper">
            <AnimatedList
                className="search-results"
                items={searchResults.results.slice(0, limit)}
                transition={null}
                transitionIn={null}
                transitionOut={null}
                bind:renderedItems
                bind:this={animatedList}
            >
                {#each renderedItems as location, index (location.data.id)}
                    <li on:click={() => navigateToResort(location.data.id)} bind:this={location.element}>
                        <div class="item-header">
                            <h2>{location.data.name}</h2>
                        </div>
                        {#if location.data.image_url}
                            <!-- svelte-ignore a11y-missing-attribute -->
                            <img src={location.data.image_url} />
                        {/if}
                        {#if location.data.description}
                            <p>{location.data.description}</p>
                        {:else}
                            <p><i>Ingen beskrivning</i></p>
                        {/if}
                        <hr />
                        <div class="middle-info">
                            <div class="slope-counts">
                                <!-- <SvelteTooltip
                                    tip={formatNumSlopes(location.data.num_green_runs, SlopeColor.Green)}
                                    bottom
                                    color="#199608"
                                > -->
                                <span class="slope-count slope-green"
                                    >{location.data.num_green_runs !== null
                                        ? location.data.num_green_runs
                                        : "?"}<GreenCircle /></span
                                >
                                <!-- </SvelteTooltip> -->
                                <!-- <SvelteTooltip
                                    tip={formatNumSlopes(location.data.num_blue_runs, SlopeColor.Blue)}
                                    bottom
                                    color="#2289d3"
                                > -->
                                <span class="slope-count slope-blue"
                                    >{location.data.num_blue_runs !== null
                                        ? location.data.num_blue_runs
                                        : "?"}<BlueSquare /></span
                                >
                                <!-- </SvelteTooltip>
                                <SvelteTooltip
                                    tip={formatNumSlopes(location.data.num_red_runs, SlopeColor.Red)}
                                    bottom
                                    color="#d32822"
                                > -->
                                <span class="slope-count slope-red"
                                    >{location.data.num_red_runs !== null ? location.data.num_red_runs : "?"}<BlueSquare
                                    /></span
                                >
                                <!-- </SvelteTooltip>
                                <SvelteTooltip
                                    tip={formatNumSlopes(location.data.num_black_runs, SlopeColor.Black)}
                                    bottom
                                    color="#000000"
                                > -->
                                <span class="slope-count slope-black"
                                    >{location.data.num_black_runs !== null
                                        ? location.data.num_black_runs
                                        : "?"}<BlackSquare /></span
                                >
                                <!-- </SvelteTooltip> -->
                            </div>
                        </div>
                    </li>
                {/each}
            </AnimatedList>
            {#if searchResults != null && limit < searchResults.results.length}
                <button
                    class="showmore"
                    on:click={() => {
                        limit *= 2;
                        if (searchResults !== null) updateSearchResults(searchResults);
                    }}
                >
                    Visa fler
                    <ChevronDownIcon />
                </button>
            {/if}
        </div>
    {/if}
</main>

<svelte:head>
    <title>{title}</title>
    <meta name="description" content={description} />
</svelte:head>

<style>
    main {
        display: flex;
        align-items: center;
        flex-direction: column;
    }

    .autocomplete-input {
        font: inherit;
        align-self: stretch;
        margin-top: 30px;
        padding: 18px;
        border-radius: 4px;
        font-size: 20px;
        overflow: hidden;
        border: none;
        background: var(--off-white);
    }

    @media (max-width: 800px) {
        .autocomplete-input {
            margin-right: 16px;
            margin-left: 16px;
            align-self: stretch;
        }
    }
</style>
